import { CaretLeft, CaretRight } from '@phosphor-icons/react'
import clsx from 'clsx'
import { EmblaCarouselType } from 'embla-carousel'
import useEmblaCarousel from 'embla-carousel-react'
import { GetStaticPropsContext } from 'next'
import Link from 'next/link'
import { useCallback, useEffect, useState } from 'react'
import { ImageType, MagazineBlock } from '../../@types/statamic'
import Picture from '../../components/elements/Picture'
import Container from '../../components/layout/Container'
import MagazineQuery from '../../graphql/MagazineQuery.graphql'
import { request, statamicConfig } from '../../StatamicGraphQLClient'
import { SORTED_MAGAZINE_TAGS } from '@/blocks/MagazineHero/Component'
import { PageParams, PreviewData } from '@/src/@types/infrastructur'
import { PageInfo } from '@/src/@types/models'
import Button from '@/src/components/core/Button'

export function getTitle({ title }: MagazineBlock) {
  return title
}

export function getImage({ image }: MagazineBlock) {
  return image
}

export async function getStaticProps(
  { limit, category, ...props }: MagazineBlock,
  page: GetStaticPropsContext<PageParams, PreviewData>
) {
  const {
    entries: { data: entries },
  } = await request<{
    entries: {
      data: {
        uri: string
        title: string
        image: ImageType
        preview_text?: string
        date: string
        tags: string[] | null
        reading_time?: number
      }[]
    }
  }>(
    MagazineQuery,
    {
      limit,
      locale: `${process.env.NEXT_PUBLIC_STATAMIC_SITE_HANDLE}_de`,
      category: category ? category.value : 'magazine',
      collection: statamicConfig.magazineCollection,
    },
    page.previewData?.statamicToken
  )

  return {
    ...props,
    entries,
  }
}

export default function Component({
  pageInfo,
  ...magazineBlock
}: Awaited<ReturnType<typeof getStaticProps>> & {
  pageInfo: PageInfo
}) {
  return (
    <div
      className={clsx({
        'pt-12 md:pt-16 md:pb-12 lg:pt-20 lg:pb-16': !pageInfo.newSpacing,
        'mt-20 md:mt-32': pageInfo.newSpacing,
      })}
    >
      <Container fullPageOnMobile>
        <div className="lg:hidden">
          <Mobile {...magazineBlock} />
        </div>
        <div className="hidden lg:block">
          <Desktop {...magazineBlock} />
        </div>
      </Container>
    </div>
  )
}

function Mobile({
  title,
  link_link,
  link_text,
  entries,
}: Awaited<ReturnType<typeof getStaticProps>>) {
  const [emblaRef, emblaApi] = useEmblaCarousel()
  const [prevDisabled, setPrevDisabled] = useState(true)
  const [nextDisabled, setNextDisabled] = useState(true)

  const onSelect = useCallback((emblaApi: EmblaCarouselType) => {
    setPrevDisabled(!emblaApi.canScrollPrev())
    setNextDisabled(!emblaApi.canScrollNext())
  }, [])

  useEffect(() => {
    if (!emblaApi) return

    onSelect(emblaApi)
    emblaApi.on('reInit', onSelect).on('select', onSelect)
  }, [emblaApi, onSelect])

  const prev = useCallback(() => {
    if (!emblaApi) return
    emblaApi.scrollPrev()
  }, [emblaApi])

  const next = useCallback(() => {
    if (!emblaApi) return
    emblaApi.scrollNext()
  }, [emblaApi])

  return (
    <>
      <h2 className="mx-5 text-4xl font-black">{title}</h2>
      <div className="mt-6" ref={emblaRef}>
        <div className="flex">
          {entries.map((entry) => (
            <Link
              key={entry.uri}
              className="block min-w-0 flex-[0_0_calc(100%-12px)] border-r border-gray-150 px-5 last:border-none"
              href={entry.uri}
            >
              <article>
                <div className="relative aspect-16/9 w-full">
                  <Picture
                    {...entry.image}
                    sizes="320px"
                    fill
                    className="rounded-lg object-cover"
                  />
                  {entry.reading_time && (
                    <p className="absolute top-2 right-2 block rounded bg-gray-800 px-3 pt-1.5 pb-1.25 text-sm text-white shadow-sm">
                      {entry.reading_time} Min.
                    </p>
                  )}
                </div>
                {entry.tags && (
                  <div className="mt-4 flex flex-wrap">
                    {entry.tags
                      .sort(
                        (a, b) =>
                          SORTED_MAGAZINE_TAGS.indexOf(a) -
                          SORTED_MAGAZINE_TAGS.indexOf(b)
                      )
                      .map((tag, index) => (
                        <>
                          <p key={tag} className="text-sm text-gray-600">
                            {tag}{' '}
                          </p>
                          {index < entry.tags!.length - 1 && (
                            <p className="mx-1 text-sm text-gray-600">·</p>
                          )}
                        </>
                      ))}
                  </div>
                )}
                <h3 className="mt-1 text-xl font-black">{entry.title}</h3>
                {entry.preview_text && (
                  <p className="mt-3">{entry.preview_text}</p>
                )}
              </article>
            </Link>
          ))}
        </div>
      </div>
      <div className="mx-5 mt-6 flex items-center justify-between">
        {link_link?.url && link_text && (
          <Link href={link_link.url} className="font-bold text-purple-600">
            {link_text}
          </Link>
        )}
        <div className="flex justify-end space-x-3">
          <Button
            size="icon-small"
            icon={<CaretLeft weight="bold" />}
            onClick={prev}
            version="gray-outlined"
            disabled={prevDisabled}
          />
          <Button
            size="icon-small"
            icon={<CaretRight weight="bold" />}
            onClick={next}
            version="gray-outlined"
            disabled={nextDisabled}
          />
        </div>
      </div>
    </>
  )
}

function Desktop({
  title,
  link_link,
  link_text,
  entries,
}: Awaited<ReturnType<typeof getStaticProps>>) {
  return (
    <>
      <div className="flex items-center justify-between">
        <h2 className="text-20xl font-black">{title}</h2>
        {link_link?.url && link_text && (
          <Link href={link_link.url} className="font-bold text-purple-600">
            {link_text}
          </Link>
        )}
      </div>
      <div className="mt-6 grid grid-cols-3 gap-8 md:mt-10">
        {entries.map((entry) => (
          <Link key={entry.uri} href={entry.uri} className="group">
            <article>
              <div className="relative aspect-16/9 w-full overflow-hidden rounded-lg border border-gray-150">
                <Picture
                  {...entry.image}
                  sizes="320px"
                  fill
                  className="object-cover transition-transform duration-300 group-hover:scale-105"
                />
                {entry.reading_time && (
                  <p className="absolute top-2 right-2 block rounded bg-gray-800 px-3 pt-1.5 pb-1.25 text-sm text-white shadow-sm">
                    {entry.reading_time} Min.
                  </p>
                )}
              </div>
              {entry.tags && (
                <div className="mt-4 flex flex-wrap">
                  {entry.tags
                    .sort(
                      (a, b) =>
                        SORTED_MAGAZINE_TAGS.indexOf(a) -
                        SORTED_MAGAZINE_TAGS.indexOf(b)
                    )
                    .map((tag, index) => (
                      <>
                        <p key={tag} className="text-sm text-gray-600">
                          {tag}{' '}
                        </p>
                        {index < entry.tags!.length - 1 && (
                          <p className="mx-1 text-sm text-gray-600">·</p>
                        )}
                      </>
                    ))}
                </div>
              )}
              <h3 className="mt-1 text-2xl font-black">{entry.title}</h3>
              {entry.preview_text && (
                <p className="mt-3">{entry.preview_text}</p>
              )}
            </article>
          </Link>
        ))}
      </div>
    </>
  )
}
